import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpErrorResponse } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { FetcherParams } from './fetcher.types';
import { CustomMessageService } from '../custom-message.service';

import { catchError, map, share } from 'rxjs/operators';
import {
  USER_FEATURE_KEY,
  USER_PREFERENCES_KEY,
  UserPreferencesFacade,
  UserPreferencesState,
  UserState,
} from '@cpak/main-features-lib/data-store';
import { LocalStorageService } from '../local-storage.service';
import { acceptTypes, headers } from '@cpak/consts';

@Injectable({
  providedIn: 'root',
})
export class FetcherService {
  private headerWithUpdatedLanguage = { ...headers };
  constructor(
    private fetcher: HttpClient,
    private customMessageService: CustomMessageService,
    private userPreferencesFacade: UserPreferencesFacade,
    private localStorageService: LocalStorageService
  ) {
    this.userPreferencesFacade.getLanguage().subscribe(language => this.setAcceptLanguageDefaultHeader(language));
    const user = this.localStorageService.getItemWithoutKey<UserState>(USER_FEATURE_KEY);
    if (user && user.userId) {
      const userPreferences = this.localStorageService.getItemWithoutKey<UserPreferencesState>(
        user.userId + '-' + USER_PREFERENCES_KEY
      );
      if (userPreferences && userPreferences.language) {
        this.setAcceptLanguageDefaultHeader(userPreferences.language);
      }
    }
  }

  static postProcessResponse<R>(request: Observable<HttpResponse<R>>): Observable<R | null> {
    return request.pipe(
      map(response => {
        return response.body;
      }),
      share()
    );
  }

  public get<R>(url: string, params: FetcherParams = {}): Observable<R | null> {
    return FetcherService.postProcessResponse<R>(this.fetcher.get<R>(url, this.prepareOptions(params))).pipe(
      catchError(error => this.handleErrors(error))
    );
  }

  public delete<R>(url: string, params: FetcherParams = {}): Observable<R | null> {
    return FetcherService.postProcessResponse<R>(this.fetcher.delete<R>(url, this.prepareOptions(params))).pipe(
      catchError(error => this.handleErrors(error))
    );
  }

  public post<R>(
    url: string,
    body?: unknown,
    params: FetcherParams = {},
    skipErrorHandling: boolean = false
  ): Observable<R | null> {
    return FetcherService.postProcessResponse<R>(this.fetcher.post<R>(url, body, this.prepareOptions(params))).pipe(
      catchError(error => {
        console.log(error);
        if (skipErrorHandling) {
          return throwError(() => error);
        }
        return this.handleErrors(error);
      })
    );
  }

  public put<R>(url: string, body?: unknown, params: FetcherParams = {}): Observable<R | null> {
    return FetcherService.postProcessResponse<R>(this.fetcher.put<R>(url, body, this.prepareOptions(params))).pipe(
      catchError(error => this.handleErrors(error))
    );
  }

  public putFormDataWithFile<R>(url: string, body: FormData): Observable<R | null> {
    const options = this.prepareOptions({});
    options.headers = options.headers.delete('Content-Type');
    return FetcherService.postProcessResponse<R>(this.fetcher.put<R>(url, body, options)).pipe(
      catchError(error => this.handleErrors(error))
    );
  }

  public patch<R>(url: string, body?: unknown, params: FetcherParams = {}): Observable<R | null> {
    return FetcherService.postProcessResponse<R>(this.fetcher.patch<R>(url, body, this.prepareOptions(params))).pipe(
      catchError(error => this.handleErrors(error))
    );
  }

  public patchFormDataWithFile<R>(url: string, body: FormData): Observable<R | null> {
    const options = this.prepareOptions({});
    options.headers = options.headers.delete('Content-Type');
    return FetcherService.postProcessResponse<R>(this.fetcher.patch<R>(url, body, options)).pipe(
      catchError(error => this.handleErrors(error))
    );
  }

  public postFormData(url: string, body: FormData): Observable<HttpResponse<string>> {
    const options = this.prepareOptions({});
    options.headers = options.headers.delete('Content-Type');
    return this.fetcher
      .post(url, body, { ...options, responseType: 'text' })
      .pipe(catchError(error => this.handleErrors(error)));
  }

  public exportExcel(
    url: string,
    body?: unknown,
    params: FetcherParams = {},
    skipErrorHandling: boolean = false
  ): Observable<HttpResponse<Blob | null>> {
    return this.download(url, 'excel', body, params, skipErrorHandling);
  }

  public download<T = unknown>(
    url: string,
    format: keyof typeof acceptTypes,
    body?: T,
    params: FetcherParams = {},
    skipErrorHandling: boolean = false
  ): Observable<HttpResponse<Blob | null>> {
    return this.fetcher
      .post(url, body, {
        headers: new HttpHeaders({
          ...this.headerWithUpdatedLanguage,
          Accept: acceptTypes[format],
        }),
        observe: 'response' as const,
        responseType: 'blob' as const,
        params: { ...params },
        withCredentials: true,
      })
      .pipe(
        catchError(error => {
          console.log(error);
          if (skipErrorHandling) {
            return throwError(() => error);
          }
          return this.handleErrors(error);
        })
      );
  }

  // eslint-disable-next-line
  private prepareOptions(params: FetcherParams) {
    return {
      headers: new HttpHeaders(this.headerWithUpdatedLanguage),
      observe: 'response' as const,
      responseType: 'json' as const,
      params: { ...params },
      withCredentials: true,
    };
  }

  protected handleErrors(errorData: { error: HttpErrorResponse }): Observable<never> {
    const { error } = errorData;

    //TODO: solution for different errors

    // const message = error.error.error?.message || error.message;
    // this.customMessageService.showErrorMessage(`Error code ${error.status}: ${message}`);
    // console.error(`Backend returned code ${error.status}, body was: ${message}`);

    const showMessage = error.status >= 500;
    showMessage &&
      this.customMessageService.showErrorMessageLostConnection(
        'Connection was lost, so your action was not saved. Please try again later.',
        'long-toast-message'
      );
    return throwError(() => errorData);
  }

  private setAcceptLanguageDefaultHeader(lang: string): void {
    this.headerWithUpdatedLanguage['Accept-Language'] = lang;
  }
}
